"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";

import { useCommandSession } from "@/components/aios-command";

const CHAT_BASE = "/workforce/command-team/chat";

export function buildCommandV2ChatPath(
    agentId: string,
    options?: { sessionId?: string | null; projectId?: string | null }
) {
    const params = new URLSearchParams();
    if (options?.sessionId) params.set("session_id", options.sessionId);
    if (options?.projectId) params.set("project_id", options.projectId);
    const query = params.toString();
    return `${CHAT_BASE}/${agentId}${query ? `?${query}` : ""}`;
}

/** Agent home — no session or project query params (used when switching agents). */
export function buildCommandV2AgentHomePath(agentId: string) {
    return buildCommandV2ChatPath(agentId);
}

interface NavigateToSessionOptions {
    urlAgentId?: string | null;
    messageAgentId?: string | null;
    projectId?: string | null;
}

export function useCommandV2ChatNavigation(agentId: string) {
    const router = useRouter();
    const { handleSessionSelect } = useCommandSession();

    const navigateToSession = useCallback(
        (
            sessionId: string | null,
            agentOrOptions?: string | null | NavigateToSessionOptions,
            projectId?: string | null
        ) => {
            let urlAgentId = agentId;
            let messageAgentId: string | null | undefined;
            let resolvedProjectId: string | undefined;

            if (
                agentOrOptions !== null &&
                agentOrOptions !== undefined &&
                typeof agentOrOptions === "object"
            ) {
                urlAgentId =
                    agentOrOptions.urlAgentId ??
                    agentOrOptions.messageAgentId ??
                    agentId ??
                    "";
                messageAgentId = agentOrOptions.messageAgentId ?? urlAgentId;
                resolvedProjectId = agentOrOptions.projectId ?? undefined;
            } else {
                urlAgentId = agentOrOptions ?? agentId ?? "";
                messageAgentId = urlAgentId;
                resolvedProjectId = projectId ?? undefined;
            }

            if (sessionId) {
                handleSessionSelect(sessionId, messageAgentId ?? undefined);
            } else {
                handleSessionSelect(null);
            }

            if (!urlAgentId) return;

            router.push(
                buildCommandV2ChatPath(urlAgentId, {
                    sessionId,
                    projectId: resolvedProjectId,
                })
            );
        },
        [agentId, handleSessionSelect, router]
    );

    const navigateToProject = useCallback(
        (projectId: string, projectAgentId?: string | null) => {
            const targetAgentId = projectAgentId || agentId;
            handleSessionSelect(null);
            router.push(
                buildCommandV2ChatPath(targetAgentId, {
                    projectId,
                })
            );
        },
        [agentId, handleSessionSelect, router]
    );

    return { navigateToSession, navigateToProject };
}
